import {
  boolean,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Report Configs — per-tenant auto-report settings
// ---------------------------------------------------------------------------

export interface ReportBrandingConfig {
  logoR2Key?: string;
  primaryColor?: string;
  companyName?: string;
  /**
   * AN-013 — when true the renderer MUST suppress the Stero/Ease platform
   * footer ("Generated by Ease Experience Platform…") so an agency or
   * white-label tenant can ship a fully rebranded export. Default `false`
   * (footer rendered) keeps Stero-attribution behaviour stable for every
   * non-white-label tenant.
   */
  hidePlatformBranding?: boolean;
}

export const REPORT_SECTIONS = [
  "executive_summary",
  "email_performance",
  "sms_whatsapp_performance",
  "social_media",
  "pipeline_health",
  "booking_utilization",
  "reputation",
  "courses",
  "learning_diagnostics",
  "attribution",
  "workflows",
  "month_over_month",
] as const;

export type ReportSection = (typeof REPORT_SECTIONS)[number];

/**
 * AN-015 — per-recipient ACL for agency fanout. Surfaces on the report
 * config so the fanout job knows which sections each recipient sees and
 * which role gates apply. Stored as JSON so Phase 1 can land without
 * a new table.
 *
 * - `agency_manager` — receives the consolidated agency report (all
 *   managed tenants rolled up + the agency-commentary section).
 * - `operator` — receives only their own tenant's report (cross-tenant
 *   leak guard).
 * - `agency_client` — receives their own tenant's report WITHOUT the
 *   agency-commentary section (per AN-015 decision packet §"Per-section
 *   visibility ACL").
 */
export const REPORT_RECIPIENT_TYPES = [
  "agency_manager",
  "operator",
  "agency_client",
] as const;

export type ReportRecipientType = (typeof REPORT_RECIPIENT_TYPES)[number];

export interface ReportRecipientAcl {
  email: string;
  recipientType: ReportRecipientType;
  /**
   * Subset of `REPORT_SECTIONS` this recipient should see. Empty array
   * means "all sections on the parent config" — saves config when the
   * recipient receives the full report.
   */
  sections: ReportSection[];
}

export const reportConfigs = pgTable("report_configs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  /**
   * AN-015 — agency org owning this config. NULL = single-tenant config
   * (legacy shape; cron iterates one-row-per-tenant). Non-null = agency
   * fanout template; the cron walks `agency_managed_tenants` and calls
   * `generateForTenant` per managed tenant with try/catch.
   */
  agencyId: uuid("agency_id"),
  /**
   * AN-015 — child rows reference their parent fanout template so the
   * fanout job can detect drift between template recipients and child
   * recipients. NULL for top-level / single-tenant configs.
   */
  parentReportConfigId: uuid("parent_report_config_id"),
  reportType: text("report_type").notNull().default("monthly"),
  sections: jsonb("sections").$type<ReportSection[]>().default([...REPORT_SECTIONS]).notNull(),
  /**
   * Legacy flat email list. Kept for backward compatibility with the
   * single-tenant cron path; AN-015 fanout reads `recipientAcl` instead.
   */
  recipients: jsonb("recipients").$type<string[]>().default([]).notNull(),
  /**
   * AN-015 — typed recipient list with role + per-recipient section
   * visibility. NULL means "use legacy flat `recipients` array"; non-null
   * takes precedence so the fanout path is the authoritative ACL.
   */
  recipientAcl: jsonb("recipient_acl").$type<ReportRecipientAcl[]>(),
  brandingConfig: jsonb("branding_config").$type<ReportBrandingConfig>().default({}).notNull(),
  isEnabled: boolean("is_enabled").notNull().default(true),
  scheduleCron: text("schedule_cron"),
  nextRunAt: timestamp("next_run_at", { withTimezone: true }),
  lastRunAt: timestamp("last_run_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_report_configs_tenant_type").on(table.tenantId, table.reportType),
  index("idx_report_configs_next_run_at").on(table.tenantId, table.nextRunAt),
  /**
   * AN-015 — cron lookup index. Lets the fanout job stream just the
   * agency-template rows without scanning the entire table.
   */
  index("idx_report_configs_agency").on(table.agencyId, table.nextRunAt),
  /**
   * AN-015 — fanout drift detection lookup. Resolve all child rows for
   * a given parent template in one indexed read.
   */
  index("idx_report_configs_parent").on(table.parentReportConfigId),
]);

// ---------------------------------------------------------------------------
// Report Runs — generated report history
// ---------------------------------------------------------------------------

export const REPORT_STATUSES = ["generating", "completed", "failed"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const reportRuns = pgTable("report_runs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  reportConfigId: uuid("report_config_id").notNull(),
  reportMonth: text("report_month").notNull(), // "2026-03"
  status: text("status").notNull().default("generating"),
  r2Key: text("r2_key"),
  generatedAt: timestamp("generated_at", { withTimezone: true }),
  sentAt: timestamp("sent_at", { withTimezone: true }),
  expiresAt: timestamp("expires_at", { withTimezone: true }), // R2 signed URL expiry
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_report_runs_month").on(table.tenantId, table.reportConfigId, table.reportMonth),
  index("idx_report_runs_tenant").on(table.tenantId, table.status),
]);
